import { memo, useMemo } from 'react';

import { Spinner } from '@air/primitive-spinner';

import { countBoards, renderBoardTree } from '~/components/Modals/BoardTemplateModal/boardTemplateHelpers';
import { MAX_BOARDS_CREATE } from '~/components/Modals/BoardTemplateModal/constants';
import { type BoardTemplate } from '~/components/Modals/BoardTemplateModal/types';

export type BoardTemplateModalGeneratePreviewProps = {
  isGenerating: boolean;
  generatedBoards: BoardTemplate[] | null;
};

export const BoardTemplateModalGeneratePreview = memo(
  ({ isGenerating, generatedBoards }: BoardTemplateModalGeneratePreviewProps) => {
    const boardCount = useMemo(() => (generatedBoards ? countBoards(generatedBoards) : 0), [generatedBoards]);
    const exceedsLimit = boardCount > MAX_BOARDS_CREATE;

    if (isGenerating) {
      return (
        <div className="flex w-1/2 flex-grow items-center justify-center">
          <Spinner className="size-8 text-blue-9" />
        </div>
      );
    }

    if (generatedBoards) {
      return (
        <div className="w-1/2">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-semibold text-14 text-grey-12">Recommended Structure:</h3>
            <span className="text-12 text-grey-11">{boardCount} boards</span>
          </div>
          {exceedsLimit && (
            <div className="mb-2 rounded-8 border border-red-6 bg-red-1 p-3 text-12 text-red-11">
              This structure has {boardCount} boards, which exceeds the limit of {MAX_BOARDS_CREATE} boards per
              operation. Please try again with a simpler structure.
            </div>
          )}
          <div className="rounded-8 border border-grey-5 bg-grey-1">
            <div className="max-h-96 overflow-y-auto pt-2 pl-4 font-mono text-12">
              {renderBoardTree(generatedBoards)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex w-1/2 flex-grow items-center justify-center rounded-8 border border-grey-5 bg-grey-1 text-14 text-grey-9">
        Generated structure will appear here
      </div>
    );
  },
);

BoardTemplateModalGeneratePreview.displayName = 'BoardTemplateModalGeneratePreview';
